import { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, X, ChevronRight, Truck } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/lib/api';
import { useCurrentProject } from '@/contexts/CurrentProjectContext';
import { scorePlateQuery } from '@/lib/plateMatch';

// PR-DROPDOWN-SCOPE MAY 12 — searchable vehicle picker grouped by project.
// preferProjectScope expands that group and shows it first; the other group
// is collapsed by default. Search still searches across both. Each row
// surfaces the current driver name so the operator can recognise the
// vehicle by who drives it.

export interface VehicleOption {
  id: string;
  code: string;
  plateEn: string | null;
  projectId: string | null;
  /** Any project's code. Was limited to two, which hid every other side. */
  projectCode: string | null;
  /** Out-of-service trucks are listed but marked, never silently dropped. */
  status: string | null;
  /** So a form can shape itself to the truck - the inspection sheet does. */
  vehicleType: string | null;
  currentDriver: { id: string; code: string | null; fullNameEn: string | null } | null;
}

type Scope = 'neom' | 'riyadh' | 'all';

interface Props {
  value: string | null;
  onChange: (id: string | null, vehicle: VehicleOption | null) => void;
  preferProjectScope?: Scope;
  placeholder?: string;
  className?: string;
}

interface RawVehicle {
  id: string;
  code: string;
  plateEn: string | null;
  projectId: string | null;
  status?: string | null;
  vehicleType?: string | null;
  currentDriver?: { id: string; code: string | null; fullNameEn: string | null } | null;
}

// Project id → human label. Cached after first load.
/** Project id -> code and name, for every project. Cached after first load. */
const projectCache = new Map<string, { code: string; name: string }>();

export function VehiclePicker({
  value, onChange,
  preferProjectScope = 'all',
  placeholder = 'Pick a vehicle…',
  className,
}: Props) {
  // Which side the app is currently on. Used to order the groups, never to
  // filter them - filtering by it is what hid two thirds of the fleet.
  const { project } = useCurrentProject();
  const [items, setItems] = useState<VehicleOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Both groups start expanded if scope=all; otherwise the non-preferred
  // group is collapsed.
  // Which non-preferred groups the operator has opened, by key. One shared
  // flag used to open all of them together.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  useEffect(() => { setOpenGroups({}); }, [preferProjectScope]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Always fetch ALL vehicles regardless of preferProjectScope — the picker
    // still needs the "other" group for the collapsed section + search.
    (async () => {
      try {
        if (projectCache.size === 0) {
          const r = await apiClient.get<{ items: Array<{ id: string; code: string }> }>(
            '/api/v1/projects',
          );
          for (const p of (r.data.items ?? []) as Array<{ id: string; code: string; name_en?: string; nameEn?: string }>) {
            // Every project, not a hand-written two: a side left out of this
            // list had its rows silently filed under OTHER.
            projectCache.set(p.id, { code: p.code, name: p.name_en ?? p.nameEn ?? p.code });
          }
        }
        const r = await apiClient.get<{ items?: RawVehicle[] } | RawVehicle[]>(
          '/api/v1/vehicles?pageSize=500&projectScope=all',
        );
        const raw: RawVehicle[] = Array.isArray(r.data) ? r.data : (r.data.items ?? []);
        const normalized: VehicleOption[] = raw.map((v) => ({
          id: v.id,
          code: v.code,
          plateEn: v.plateEn,
          projectId: v.projectId,
          projectCode: v.projectId ? (projectCache.get(v.projectId)?.code ?? null) : null,
          status: v.status ?? null,
          vehicleType: v.vehicleType ?? null,
          currentDriver: v.currentDriver ?? null,
        }));
        if (!cancelled) setItems(normalized);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // PR-R1 — hard project isolation: when a specific project is preferred, never
  // surface another project's vehicles (belt-and-suspenders on top of the
  // header-scoped /vehicles list).
  const scopeCode = preferProjectScope === 'neom' ? 'PRJ-NEOM'
    : preferProjectScope === 'riyadh' ? 'PRJ-RIYADH' : null;
  const filtered = useMemo(() => {
    const q = query.trim();
    // PR-MAY15-LEDGERS-V3 item 2 — plate-first ranking. Score every
    // vehicle on plate match; vehicles whose plate matches the query
    // score 20-100, vehicles that only match code/driver name fall
    // through to 1 (kept but bumped to the end). Sort: score DESC,
    // then plate ASC for stable order.
    const qLower = q.toLowerCase();
    const ranked = items
      .map((v) => {
        let score = scorePlateQuery(q, v.plateEn);
        if (score === 0) {
          // Fall back to code/driver match with a low score so plate
          // matches always sort first.
          const codeMatch   = v.code.toLowerCase().includes(qLower);
          const driverMatch = (v.currentDriver?.fullNameEn ?? '').toLowerCase().includes(qLower);
          if (codeMatch)   score = 5;
          else if (driverMatch) score = 3;
        }
        return { v, score };
      })
      .filter((x) => (!q || x.score > 0) && (!scopeCode || x.v.projectCode === scopeCode))
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return (a.v.plateEn ?? '').localeCompare(b.v.plateEn ?? '');
      })
      .map((x) => x.v);
    return ranked;
  }, [items, query]);

  const groups = useMemo(() => {
    // Built from the rows, not from a fixed list: anything filed under a
    // project gets that project's own group, and only genuinely unfiled rows
    // fall through to OTHER.
    const byProject = new Map<string, VehicleOption[]>();
    const other: VehicleOption[] = [];
    for (const v of filtered) {
      if (v.projectCode) {
        const rows = byProject.get(v.projectCode);
        if (rows) rows.push(v); else byProject.set(v.projectCode, [v]);
      } else other.push(v);
    }
    // Running trucks first, retired ones last: the common case stays at the
    // top of the list instead of being interleaved with vehicles nobody
    // should normally pick.
    const byLabel = (a: VehicleOption, b: VehicleOption) => {
      const aOut = a.status && a.status !== 'active' ? 1 : 0;
      const bOut = b.status && b.status !== 'active' ? 1 : 0;
      if (aOut !== bOut) return aOut - bOut;
      return (a.plateEn ?? a.code).localeCompare(b.plateEn ?? b.code);
    };
    byProject.forEach((rows) => rows.sort(byLabel));
    other.sort(byLabel);
    return { byProject, other };
  }, [filtered]);

  const selected = items.find((v) => v.id === value) ?? null;
  const preferred = preferProjectScope;

  const orderedSections: Array<{
    key: string;
    label: string;
    rows: typeof groups.other;
    primary: boolean;
  }> = (() => {
    // The project's own name, from the same cache the rows were tagged with.
    const labelFor = (code: string) => {
      for (const p of projectCache.values()) if (p.code === code) return p.name.toUpperCase();
      return code;
    };
    const sections = [...groups.byProject.entries()].map(([code, rows]) => ({
      key: code, label: labelFor(code), rows,
      // The side being worked on leads, without hiding the others: an explicit
      // preferProjectScope if given, otherwise whichever side is selected in
      // the app. Ordering only - everything is still reachable below.
      primary: code === (scopeCode ?? project?.code ?? null),
    }));
    // Preferred first, then alphabetical, so the order is stable between opens.
    sections.sort((a, b) =>
      (b.primary ? 1 : 0) - (a.primary ? 1 : 0) || a.label.localeCompare(b.label));
    // Last, and only if it holds anything: not a project, just the rows nobody
    // has filed.
    sections.push({ key: 'other', label: 'OTHER', rows: groups.other, primary: false });
    return sections;
  })();

  function pick(v: VehicleOption) {
    onChange(v.id, v);
    setOpen(false);
    setQuery('');
  }

  return (
    <div className={'relative ' + (className ?? '')}>
      <button
        type="button"
        // Opening always starts from a clean list. The query used to
        // survive a close, so reopening showed the remains of an
        // earlier search - a single stray character hiding most of
        // the fleet behind a box that looks empty.
        onClick={() => { if (!open) setQuery(''); setOpen((s) => !s); }}
        className="flex h-11 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-base text-left"
      >
        <span className={selected ? 'inline-flex items-center gap-2' : 'text-muted-foreground'}>
          {selected ? (
            <>
              <Truck className="h-4 w-4 text-muted-foreground" />
              {selected.plateEn ?? selected.code}
              {selected.currentDriver?.fullNameEn && (
                <span className="text-xs text-muted-foreground">
                  · {selected.currentDriver.fullNameEn}
                </span>
              )}
            </>
          ) : placeholder}
        </span>
        <span className="text-muted-foreground text-xs">▾</span>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-md border border-border bg-card shadow-lg max-h-[440px] flex flex-col">
          <div className="p-2 border-b border-border flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by plate, code, or driver…"
              className="h-8 border-0 shadow-none focus-visible:ring-0 px-1"
            />
            {query && (
              <>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                  {filtered.length} of {items.length}
                </span>
                <button type="button" onClick={() => setQuery('')}
                  className="text-xs text-muted-foreground hover:text-foreground">Clear search</button>
              </>
            )}
            {value && (
              <button type="button" onClick={() => { onChange(null, null); setOpen(false); }}
                className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-3 text-sm text-muted-foreground inline-flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-3 text-sm text-muted-foreground">No vehicles match.</div>
            ) : (
              orderedSections.map((section) => {
                if (section.rows.length === 0) return null;
                const leads = scopeCode ?? project?.code ?? null;
                const expandedDefault = section.primary || query.length > 0 || !leads;
                const open = expandedDefault || !!openGroups[section.key];
                return (
                  <div key={section.key}>
                    <button
                      type="button"
                      onClick={() => section.primary ? null
                        : setOpenGroups((g) => ({ ...g, [section.key]: !g[section.key] }))}
                      className="w-full px-3 py-1.5 bg-muted/30 flex items-center justify-between text-[11px] uppercase tracking-wider text-muted-foreground border-y border-border"
                    >
                      <span>{section.label} ({section.rows.length})</span>
                      {!section.primary && (
                        <ChevronRight className={'h-3 w-3 transition-transform ' + (open ? 'rotate-90' : '')} />
                      )}
                    </button>
                    {open && section.rows.map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => pick(v)}
                        className={'w-full text-left px-3 py-2 hover:bg-muted/40 text-sm flex items-center justify-between '
                          + (v.id === value ? 'bg-muted/30' : '')}
                      >
                        <span className="flex items-center gap-2 truncate">
                          <span className="font-mono text-xs text-muted-foreground">{v.code}</span>
                          <span>{v.plateEn ?? '—'}</span>
                          {v.status && v.status !== 'active' && (
                            <span className="text-[10px] rounded px-1.5 py-0.5 bg-amber-500/15 text-amber-600 dark:text-amber-400 whitespace-nowrap">
                              {v.status.replace(/_/g, ' ')}
                            </span>
                          )}
                          {v.currentDriver?.fullNameEn && (
                            <span className="text-xs text-muted-foreground truncate">
                              · {v.currentDriver.fullNameEn}
                            </span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Close on outside click — simple approach via backdrop */}
      {open && (
        <button
          type="button"
          aria-label="Close picker"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-20 cursor-default"
        >
          <X className="hidden" />
        </button>
      )}
    </div>
  );
}
